import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { ValidationError } from '@tanstack/react-form'
import { ReactNode } from '@tanstack/react-router'
import { useId } from 'react'
import { GenericField, GenericFieldSizing } from './generic'
import { MagicSwirl } from './magic-swirl'

export interface SelectFieldProps<T> {
	options: Array<T>
	renderOption?: (option: T) => ReactNode
	value: T
	onChange: (value: T) => void
	label?: ReactNode
	magicMode?: boolean
	hint?: ReactNode
	errors?: Array<ValidationError>
	disabled?: boolean
	isWaiting?: boolean
	sizing?: GenericFieldSizing
	/**
	 * Optional content rendered at the bottom of the options list. Useful for actions
	 * like "Create new…" or opening a secondary picker. The caller is responsible for
	 * providing appropriate markup/styling (e.g. a sticky container) and handling
	 * click behavior.
	 */
	bottomSlot?: ReactNode
	placeholder?: ReactNode
	hideLabel?: boolean
}

export function SelectField<T>(
	{
		options,
		value,
		onChange,
		renderOption = (option) => option,
		label,
		magicMode = false,
		hint,
		errors = [],
		disabled = false,
		isWaiting = false,
		sizing = 'medium',
		bottomSlot,
		placeholder,
		hideLabel = false,
	}: SelectFieldProps<T>,
) {
	const id = useId()

	const inputClassName = ctl(`
		pr-10
		block
		relative
		group
		rounded-md ring-1 ring-grey-100 p-2
		border-none border-transparent 
		data-open:border-transparent 
		data-open:outline-none
		data-open:ring-blue-500
		text-grey-800
		text-left
		w-full
		transition-all 
		${magicMode ? 'text-transparent cursor-wait bg-purple-50/50' : 'text-grey-800 bg-white'}
		${disabled ? 'opacity-50 cursor-not-allowed' : ''}
		${isWaiting ? 'opacity-50 cursor-wait' : ''}
		cursor-pointer
	`)

	return (
		<GenericField label={label} errors={errors} hint={hint} sizing={sizing} hideLabel={hideLabel}>
			<div className="has-data-open:ring-4 ring-blue-100 rounded-md w-full whitespace-normal relative">
				<Listbox value={value} onChange={onChange} name={id} disabled={magicMode || disabled || isWaiting}>
					<ListboxButton className={inputClassName}>
						{value ? renderOption(value) : (placeholder ?? <>&nbsp;</>)}
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 10 10"
							height={10}
							width={10}
							className="absolute top-1/2 right-4 -translate-y-1/2 group-data-[open]:rotate-180 transition-transform duration-300"
						>
							<path
								fill="currentColor"
								d="M5 7.874a1.238 1.238 0 0 1 -0.879 -0.364L0.206 3.393A0.75 0.75 0 0 1 1.294 2.36l3.525 3.712a0.249 0.249 0 0 0 0.362 0L8.706 2.36a0.75 0.75 0 1 1 1.088 1.033L5.9 7.5a1.27 1.27 0 0 1 -0.9 0.374Z"
								strokeWidth={1}
							/>
						</svg>
					</ListboxButton>
					<ListboxOptions
						anchor="bottom"
						className={ctl(`
							min-w-[calc(var(--button-width)+8px)] [--anchor-gap:8px] 
							ring-1 py-1 bg-white ring-grey-100 shadow-md rounded
							origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0
						`)}
						transition
					>
						{options.map((option) => (
							<ListboxOption
								key={JSON.stringify(option)}
								value={option}
								className="text-sm rounded mx-1 pl-2 pr-10 py-2 group flex gap-2 bg-white data-[focus]:bg-blue-100 data-[focus]:text-blue-700 cursor-pointer whitespace-nowrap"
							>
								{renderOption(option)}
							</ListboxOption>
						))}
						{bottomSlot ?? null}
					</ListboxOptions>
				</Listbox>
				{magicMode ? <MagicSwirl /> : null}
			</div>
		</GenericField>
	)
}
